import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useFetch } from "../../../hooks/useFecth";
import { useEffect, useState, useContext } from "react";
import { AppContext } from "../../../context/AppContext";
import { FormControl, InputLabel, MenuItem, Select } from "@mui/material";

import DataTable from "react-data-table-component";
import HandlerApp from "../../../utils/handlerApp";

const PRIORITY_CODES = [
    { code: "31", label: "31 - CMOS RAM error (recovered)" },
    { code: "32", label: "32 - CMOS RAM error (no data)" },
    { code: "33", label: "33 - CMOS RAM error (bad device)" },
    { code: "34", label: "34 - EEPROM error" },
    { code: "35", label: "35 - EEPROM bad device" },
    { code: "36", label: "36 - EPROM version changed" },
    { code: "37", label: "37 - EPROM bad checksum" },
    { code: "38", label: "38 - Partitioned EPROM changed" },
    { code: "39", label: "39 - Partitioned EPROM bad checksum" },
    { code: "17", label: "17 - AC power applied" },
    { code: "18", label: "18 - AC power lost" },
    { code: "3A", label: "3A - Memory error reset" },
    { code: "3B", label: "3B - Low backup battery" },
];

const PrioritySAS = () => {
    const endpoint = "/administration/locations";
    const parent = "Reports";
    const [t] = useTranslation("global");
    const title = t("reports.SAS report");
    const api = useFetch();
    const handlerApp = HandlerApp();
    const { app } = useContext(AppContext);

    const [permissions, setPermissions] = useState([]);
    const [menus, setMenus] = useState([]);
    const [machines, setMachines] = useState([]);
    const [machine, setMachine] = useState("");
    const [errors, setErrors] = useState({ machine: false });

    const [sasData, setSasData] = useState([]);
    const [resetPaginationToggle, setResetPaginationToggle] = useState(false);

    const [filterCode, setFilterCode] = useState("");
    const [filterInitDate, setFilterInitDate] = useState("");
    const [filterEndDate, setFilterEndDate] = useState("");

    useEffect(() => {
        setMenus(app.menus[parent]);
        setPermissions(app.permissions[endpoint]);
    }, [app]);

    useEffect(() => {
        sendRequest();
    }, []);

    const sendRequest = () => {
        handlerApp.setLoader(true);
        api.get(endpoint, { action: "getParamsUpdateMachines" })
            .then((response) => {
                if (response.success) {
                    setMachines(handlerApp.getResultFromObject(response.data, "machines"));
                } else {
                    handlerApp.handlerResponse(response);
                }
                handlerApp.setLoader(false);
            })
            .catch((error) => {
                handlerApp.showError(t(error.message));
                handlerApp.setLoader(false);
            });
    };

    const fetchSASData = (machineCode) => {
        if (!machineCode) return;
        handlerApp.setLoader(true);

        api.post("reports/sas/priorityList", {
            machine: machineCode,
            code: filterCode,
            startDate: filterInitDate,
            endDate: filterEndDate,
        })
            .then((response) => {
                if (response.success && Array.isArray(response.data)) {
                    const sorted = response.data.sort(
                        (a, b) => new Date(b.code_sas_date) - new Date(a.code_sas_date)
                    );
                    setSasData(sorted);
                } else {
                    setSasData([]);
                }
                handlerApp.setLoader(false);
            })
            .catch((error) => {
                handlerApp.showError(t(error.message));
                handlerApp.setLoader(false);
            });
    };

    const columns = [
        {
            id: "uniqueid",
            name: "UniqueID",
            selector: (row) => row.uniqueid,
            sortable: true,
            width: "150px",
        },
        {
            id: "code_sas_date",
            name: "Date",
            selector: (row) =>
                new Date(row.code_sas_date).toLocaleString("en-US", {
                    dateStyle: "short",
                    timeStyle: "short",
                }),
            sortable: true,
            width: "200px",
        },
        {
            id: "description_en",
            name: "Description (EN)",
            selector: (row) => row.description_en || "-",
            sortable: true,
        },
        {
            id: "description_es",
            name: "Descripción (ES)",
            selector: (row) => row.description_es || "-",
            sortable: true,
        },
    ];

    return (
        <div className="app container">
            {/* <!-- titulo --> */}
            <div className="d-flex justify-content-between">
                <section className="section-title-page">
                    <h3 className="title-page">{title}</h3>
                    <p className="light">{t("reports.SAS description")}</p>
                </section>
            </div>

            {/* <!-- Menu interno --> */}
            <nav className="child-menu">
                {menus?.map((item) => (
                    <NavLink
                        key={item.menu_children}
                        to={"/" + item.menu_children_action}
                        activeclassname="active"
                    >
                        {item.menu_children}
                    </NavLink>
                ))}
            </nav>

            <section className="filters">
                <div className="row justify-content-left px-4 mb-3">
                    <div className="col-md-3 d-flex flex-column justify-content-center">
                        <label className="form-label">{t("locationComponent.machine")}</label>
                        <FormControl className="w-100 bg-white" size="small">
                            <InputLabel id="machine-label">{t("reports.machines")}</InputLabel>
                            <Select
                                labelId="machine-label"
                                value={machine?.code || ""}
                                label={t("reports.machines")}
                                onChange={(e) => {
                                    setErrors({ ...errors, machine: !e.target.value });
                                    const selectedCode = e.target.value;
                                    const selected = machines.find((m) => m.code === selectedCode);
                                    const nameSelect = machines.find((m) => m.code === e.target.value)?.name || "";
                                    setMachine(selected);
                                    fetchSASData(nameSelect);
                                }}
                            >
                                <MenuItem value="">
                                    <em>...</em>
                                </MenuItem>
                                {machines.map((m) => (
                                    <MenuItem key={m.code} value={m.code}>
                                        {m.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {errors.machine && (
                            <small className="text-danger">{t("errors.required")}</small>
                        )}
                    </div>

                    <div className="col-md-3 d-flex flex-column justify-content-center">
                        <label className="form-label">{t("reports.SAS code")}</label>
                        <FormControl className="w-100 bg-white" size="small">
                            <InputLabel>{t("reports.SAS code")}</InputLabel>
                            <Select
                                value={filterCode}
                                label={t("reports.SAS code")}
                                onChange={(e) => {
                                    setFilterCode(e.target.value);
                                }}
                            >
                                <MenuItem value="">
                                    <em>...</em>
                                </MenuItem>
                                {PRIORITY_CODES.map((c) => (
                                    <MenuItem key={c.code} value={c.code}>
                                        {c.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    {/* START DATE */}
                    <div className="col-md-2">
                        <label className="form-label">{t("reports.start date")}</label>
                        <input
                            type="datetime-local"
                            className="form-control"
                            value={filterInitDate}
                            onChange={(e) => {
                                setFilterInitDate(e.target.value);
                            }}
                        />
                    </div>

                    {/* END DATE */}
                    <div className="col-md-2">
                        <label className="form-label">{t("reports.final date")}</label>
                        <input
                            type="datetime-local"
                            className="form-control"
                            value={filterEndDate}
                            min={filterInitDate}
                            onChange={(e) => {
                                setFilterEndDate(e.target.value);
                            }}
                        />
                    </div>

                    <div className="col-md-1 d-flex align-items-center gap-1">
                        <button
                            className="btn-table-actions"
                            title="Limpiar Filtros"
                            onClick={() => {
                                setFilterCode("");
                                setFilterInitDate("");
                                setFilterEndDate("");

                                if (machine?.name) {
                                    fetchSASData(machine.name);
                                }
                            }}

                        >
                            <span className="material-symbols-outlined">
                                cleaning_services
                            </span>
                        </button>
                        <button
                            className="bold-btn bold-btn-dark"
                            onClick={() => {
                                if (machine?.name) {
                                    fetchSASData(machine.name);
                                } else {
                                    setErrors({ ...errors, machine: true });
                                }
                            }}
                        >
                            {t('submit')}
                        </button>
                    </div>
                </div>

                {sasData.length > 0 && (
                    <div className="filters">
                        <DataTable
                            columns={columns}
                            data={sasData}
                            pagination
                            paginationPerPage={10}
                            paginationRowsPerPageOptions={[10, 20, 50]}
                            paginationResetDefaultPage={resetPaginationToggle}
                            highlightOnHover
                            dense
                            noDataComponent={<div>{t("noData")}</div>}
                            persistTableHead
                        />
                    </div>
                )}
            </section >
        </div >
    );
};

export default PrioritySAS;
